import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { NgIconComponent } from "@ng-icons/core";
import { isNgIconName } from "../utils/icon";

/**
 * EVA Avatar - Photo ou initiales utilisateur
 *
 * Composant 100% custom utilisant les tokens EVA.
 * Supporte image, initiales générées, badge et indicateur de statut.
 *
 * @example
 * // Avatar avec image
 * <eva-avatar src="https://example.com/photo.jpg" name="Jean Dupont"></eva-avatar>
 *
 * // Avatar avec initiales (couleur auto)
 * <eva-avatar name="Jean Dupont" size="large"></eva-avatar>
 *
 * // Avatar avec badge et statut
 * <eva-avatar name="Marie Martin" badge="3" badgeSeverity="danger" status="online"></eva-avatar>
 *
 * // Avatar icon fallback
 * <eva-avatar icon="pi pi-user" size="xlarge"></eva-avatar>
 */
@Component({
  selector: "eva-avatar",
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  templateUrl: "./eva-avatar.component.html",
  styleUrls: ["./eva-avatar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaAvatarComponent {
  protected readonly isNgIconName = isNgIconName;

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** URL de l'image */
  @Input() src: string = "";

  /** Nom complet (pour générer les initiales) */
  @Input() name: string = "";

  /** Initiales explicites (override la dérivation depuis `name`) */
  @Input() initialsOverride: string = "";

  /** Taille de l'avatar */
  @Input() size: "xsmall" | "small" | "medium" | "large" | "xlarge" = "medium";

  /** Forme de l'avatar */
  @Input() shape: "circle" | "square" = "circle";

  /** Couleur de fond personnalisée (si vide, générée à partir du nom) */
  @Input() color: string = "";

  /** Icône si pas d'image ni de nom (classe PrimeNG, ex: "pi pi-user") */
  @Input() icon: string = "";

  /** Valeur du badge (chiffre ou texte court) */
  @Input() badge: string = "";

  /** Sévérité du badge */
  @Input() badgeSeverity: "primary" | "success" | "warning" | "danger" = "primary";

  /** Indicateur de statut */
  @Input() status: "online" | "offline" | "busy" | "away" | null = null;

  // ==========================================================================
  // Computed
  // ==========================================================================

  /**
   * Génère les initiales à partir du nom
   * Ex: "Jean Dupont" -> "JD"
   */
  get initials(): string {
    if (this.initialsOverride && this.initialsOverride.trim()) {
      return this.initialsOverride.trim().substring(0, 3).toUpperCase();
    }
    if (!this.name) return "";
    return this.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  }

  /**
   * Génère une couleur de fond à partir du nom (hash simple)
   */
  get backgroundColor(): string {
    if (this.color) return this.color;
    if (!this.name) return "var(--eva-surface-quaternary)";

    // Palette tokenisée pour éviter les couleurs hardcodées.
    const colors = [
      "var(--primitives-brand-main-01)",
      "var(--primitives-brand-main-02)",
      "var(--primitives-brand-accent-01)",
      "var(--primitives-brand-accent-02)",
      "var(--eva-alert-success-primary)",
      "var(--eva-alert-danger-primary)",
      "var(--eva-alert-info-primary)",
      "var(--eva-text-primary)",
      "var(--eva-text-secondary)",
      "var(--eva-border-focus)",
    ];

    let hash = 0;
    for (let i = 0; i < this.name.length; i++) {
      hash = this.name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  /**
   * Détermine ce qui doit être affiché
   */
  get displayMode(): "image" | "initials" | "icon" {
    if (this.src) return "image";
    if (this.name || this.initialsOverride) return "initials";
    return "icon";
  }

  /**
   * Gestion de l'erreur de chargement d'image
   */
  imageError: boolean = false;

  onImageError(): void {
    this.imageError = true;
  }

  /**
   * Mode d'affichage effectif (fallback si erreur image)
   */
  get effectiveDisplayMode(): "image" | "initials" | "icon" {
    if (this.displayMode === "image" && this.imageError) {
      return this.name ? "initials" : "icon";
    }
    return this.displayMode;
  }
}
